import AddIcon from '@mui/icons-material/Add'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import {
  useConsultants,
  useDisciplines,
  useDrawingTypes,
  useDrawingsForReport,
  useDeleteDrawingRevision,
  useRecordDrawing,
  useUpdateDrawingRevision,
  type DrawingReceived,
} from '../../api/execution'
import { ErrorBlock, LoadingBlock } from '../../components/Feedback'

/**
 * Drawings received at site today — from the architect, structural engineer,
 * MEP consultant, kitchen consultant and anyone else who issues drawings.
 */
export function DrawingsTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = useDrawingsForReport(reportId)
  const record = useRecordDrawing(reportId)
  const updateRevision = useUpdateDrawingRevision(reportId)
  const deleteRevision = useDeleteDrawingRevision(reportId)
  const [open, setOpen] = useState(false)
  const [editingRevision, setEditingRevision] = useState<DrawingReceived | null>(null)
  const [confirming, setConfirming] = useState<DrawingReceived | null>(null)

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Drawings handed over today. A new revision of a drawing already on the register
          supersedes the previous one rather than creating a second entry.
        </Typography>
        {editable && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Record drawing received
          </Button>
        )}
      </Box>

      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock error={list.error} />}
      {updateRevision.error && (
        <ErrorBlock error={updateRevision.error} title="Could not save the correction" />
      )}
      {deleteRevision.error && (
        <ErrorBlock error={deleteRevision.error} title="Could not remove the drawing" />
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Drawing no.</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Discipline</TableCell>
              <TableCell>Consultant</TableCell>
              <TableCell>Rev</TableCell>
              <TableCell>Rev date</TableCell>
              <TableCell>Received from</TableCell>
              <TableCell>File</TableCell>
              {editable && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {(list.data ?? []).map((d) => (
              <TableRow key={d.revisionId} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>{d.drawingNumber}</TableCell>
                <TableCell>{d.title}</TableCell>
                <TableCell>{d.disciplineName ?? '-'}</TableCell>
                <TableCell>{d.consultantName ?? '-'}</TableCell>
                <TableCell>
                  <Chip label={d.revisionNumber} size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
                <TableCell>{d.revisionDate ?? '-'}</TableCell>
                <TableCell>{d.receivedFrom ?? '-'}</TableCell>
                <TableCell>{d.attachmentId ? <AttachFileIcon fontSize="small" /> : '-'}</TableCell>
                {editable && (
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton
                      size="small"
                      title="Correct what was recorded"
                      onClick={() => setEditingRevision(d)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      title="Remove this drawing from today"
                      onClick={() => setConfirming(d)}
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {(list.data?.length ?? 0) === 0 && !list.isLoading && (
              <TableRow>
                <TableCell colSpan={editable ? 9 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No drawings received today.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DrawingForm
          pending={record.isPending}
          error={record.error}
          onCancel={() => setOpen(false)}
          onSave={async (data, file) => {
            await record.mutateAsync({ data, file })
            setOpen(false)
          }}
        />
      </Dialog>

      <Dialog
        open={Boolean(editingRevision)}
        onClose={() => setEditingRevision(null)}
        fullWidth
        maxWidth="sm"
      >
        {editingRevision && (
          <RevisionEditForm
            revision={editingRevision}
            pending={updateRevision.isPending}
            onCancel={() => setEditingRevision(null)}
            onSave={async (body) => {
              await updateRevision.mutateAsync({ revisionId: editingRevision.revisionId, body })
              setEditingRevision(null)
            }}
          />
        )}
      </Dialog>

      <Dialog open={Boolean(confirming)} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove {confirming?.drawingNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This removes revision {confirming?.revisionNumber} from today's report. The drawing
            stays on the register; if this was its only revision it goes back to being pending.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteRevision.isPending}
            onClick={async () => {
              if (confirming) await deleteRevision.mutateAsync(confirming.revisionId)
              setConfirming(null)
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/**
 * Correcting a drawing already logged today.
 *
 * <p>Only what was recorded on receipt can be changed here - the number, the revision, the
 * dates and who handed it over. Which drawing it is belongs to the register, not to a day's
 * report, and is edited there.</p>
 */
function RevisionEditForm({
  revision,
  pending,
  onCancel,
  onSave,
}: {
  revision: DrawingReceived
  pending: boolean
  onCancel: () => void
  onSave: (body: Record<string, unknown>) => Promise<void>
}) {
  const [form, setForm] = useState({
    revisionNumber: revision.revisionNumber ?? 'R0',
    revisionDate: revision.revisionDate ?? '',
    receivedDate: revision.receivedDate ?? '',
    receivedFrom: revision.receivedFrom ?? '',
    remarks: revision.remarks ?? '',
  })
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value })

  return (
    <>
      <DialogTitle>
        {revision.drawingNumber}
        <Typography variant="body2" color="text.secondary">
          {revision.title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            label="Revision"
            value={form.revisionNumber}
            onChange={set('revisionNumber')}
            helperText="R0, R1, R2 ..."
          />
          <TextField
            label="Revision date"
            type="date"
            value={form.revisionDate}
            onChange={set('revisionDate')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Received date"
            type="date"
            value={form.receivedDate}
            onChange={set('receivedDate')}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField label="Received from" value={form.receivedFrom} onChange={set('receivedFrom')} />
          <TextField
            label="Remarks"
            value={form.remarks}
            onChange={set('remarks')}
            multiline
            minRows={2}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending}
          onClick={() =>
            onSave({
              revisionNumber: form.revisionNumber || undefined,
              revisionDate: form.revisionDate || undefined,
              receivedDate: form.receivedDate || undefined,
              receivedFrom: form.receivedFrom || undefined,
              remarks: form.remarks || undefined,
            })
          }
        >
          {pending ? 'Saving...' : 'Save changes'}
        </Button>
      </DialogActions>
    </>
  )
}

function DrawingForm({
  pending,
  error,
  onCancel,
  onSave,
}: {
  pending: boolean
  error: unknown
  onCancel: () => void
  onSave: (data: Record<string, unknown>, file?: File | null) => Promise<void>
}) {
  const consultants = useConsultants()
  const disciplines = useDisciplines()
  const types = useDrawingTypes()
  const [file, setFile] = useState<File | null>(null)
  const [form, setForm] = useState({
    drawingNumber: '',
    title: '',
    consultantId: '',
    disciplineId: '',
    drawingTypeId: '',
    revisionNumber: 'R0',
    revisionDate: new Date().toISOString().slice(0, 10),
    receivedDate: new Date().toISOString().slice(0, 10),
    receivedFrom: '',
    remarks: '',
  })
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value })

  // Naming the issuer in the drawing register is what makes follow-up possible later.
  const consultantName = consultants.data?.find((c) => c.id === form.consultantId)?.name

  return (
    <>
      <DialogTitle>Drawing received</DialogTitle>
      <DialogContent dividers>
        {error != null && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock error={error} title="Could not record the drawing" />
          </Box>
        )}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' } }}>
          <TextField label="Drawing number" required value={form.drawingNumber}
                     onChange={set('drawingNumber')} helperText="e.g. STR-FND-014" />
          <TextField label="Title" required value={form.title} onChange={set('title')}
                     sx={{ gridColumn: { sm: 'span 2' } }} />

          <TextField select label="Issued by (consultant)" required value={form.consultantId}
                     onChange={(e) => {
                       const id = e.target.value
                       const name = consultants.data?.find((c) => c.id === id)?.name ?? ''
                       setForm({ ...form, consultantId: id, receivedFrom: form.receivedFrom || name })
                     }}
                     sx={{ gridColumn: { sm: 'span 2' } }}>
            {(consultants.data ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.extra ? ` — ${c.extra}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Discipline" value={form.disciplineId} onChange={set('disciplineId')}>
            <MenuItem value="">Not specified</MenuItem>
            {(disciplines.data ?? []).map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </TextField>

          <TextField select label="Drawing type" value={form.drawingTypeId} onChange={set('drawingTypeId')}>
            <MenuItem value="">Not specified</MenuItem>
            {(types.data ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </TextField>
          <TextField label="Revision" value={form.revisionNumber} onChange={set('revisionNumber')}
                     helperText="R0, R1, R2 ..." />
          <TextField label="Revision date" type="date" value={form.revisionDate}
                     onChange={set('revisionDate')} slotProps={{ inputLabel: { shrink: true } }} />

          <TextField label="Received date" type="date" value={form.receivedDate}
                     onChange={set('receivedDate')} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Received from (person)" value={form.receivedFrom}
                     onChange={set('receivedFrom')} sx={{ gridColumn: { sm: 'span 2' } }}
                     helperText={consultantName ? `Issued by ${consultantName}` : ' '} />

          <TextField label="Remarks" value={form.remarks} onChange={set('remarks')}
                     multiline minRows={2} sx={{ gridColumn: { sm: '1 / -1' } }} />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button component="label" variant="outlined" startIcon={<AttachFileIcon />}>
            Attach drawing file
            <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg,.dwg"
                   onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : 'Optional — PDF or image'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={pending || !form.drawingNumber.trim() || !form.title.trim() || !form.consultantId}
          onClick={() =>
            onSave(
              {
                drawingNumber: form.drawingNumber.trim(),
                title: form.title.trim(),
                consultantId: form.consultantId,
                disciplineId: form.disciplineId || undefined,
                drawingTypeId: form.drawingTypeId || undefined,
                revisionNumber: form.revisionNumber || 'R0',
                revisionDate: form.revisionDate || undefined,
                receivedDate: form.receivedDate || undefined,
                receivedFrom: form.receivedFrom || undefined,
                remarks: form.remarks || undefined,
              },
              file,
            )
          }
        >
          {pending ? 'Saving...' : 'Record drawing'}
        </Button>
      </DialogActions>
    </>
  )
}
